import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, BellRing, MessageCircle, Cake, AlertTriangle, RotateCw } from "lucide-react";

const KIND_LABEL = {
  loan_approved: "Loan approved",
  loan_disbursed: "Loan disbursed",
  repayment_reminder: "Repayment reminder",
  welcome: "Welcome email",
  status_under_review: "Status: under review",
  status_approved: "Status: approved",
  status_rejected: "Status: rejected",
  status_disbursed: "Status: disbursed",
  payment_receipt: "Payment receipt",
  birthday: "Birthday greeting",
  overdue_reminder: "Overdue reminder",
};

const STATUS_BADGE = {
  simulated: "bg-amber-100 text-amber-900 border-amber-300",
  sent: "bg-emerald-100 text-emerald-900 border-emerald-300",
  failed: "bg-rose-100 text-rose-900 border-rose-300",
  skipped_no_consent: "bg-stone-100 text-stone-700 border-stone-300",
  skipped_no_phone: "bg-stone-100 text-stone-700 border-stone-300",
  skipped_no_recipient: "bg-stone-100 text-stone-700 border-stone-300",
  pending: "bg-blue-100 text-blue-900 border-blue-300",
};

export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [demoMode, setDemoMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get("/admin/notifications?limit=500")
      .then(({ data }) => {
        setItems(data.items || []);
        setDemoMode(Boolean(data.demo_mode));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/admin/notifications?limit=500")
      .then(({ data }) => {
        setItems(data.items || []);
        setDemoMode(Boolean(data.demo_mode));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const runReminders = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/notifications/run-reminders");
      toast.success(`Reminder scan done — ${data.sent} sent, ${data.skipped} skipped.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const runBirthdays = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/notifications/run-birthdays");
      toast.success(`Birthday scan done — ${data.sent} greeting(s) sent for ${data.for_date}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const runOverdue = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/notifications/run-overdue");
      toast.success(`Overdue scan done — ${data.sent} escalation(s) sent.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/admin/notifications/${id}/resend`);
      toast.success(`Resent — status: ${String(data.status).replace(/_/g, " ")}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Notifications</h1>
          <p className="mt-1 text-sm text-ink-soft">WhatsApp & email messages sent to borrowers — welcome, application status updates, loan approvals, disbursements and repayment reminders.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={load} variant="outline" className="border-[#5B2C82] text-[#5B2C82]" data-testid="notif-refresh-btn">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={runReminders} disabled={running} className="bg-[#5B2C82] text-white hover:bg-[#46225f]" data-testid="notif-run-reminders-btn">
            <BellRing className="mr-1.5 h-4 w-4" /> {running ? "Running…" : "Run reminders now"}
          </Button>
          <Button onClick={runBirthdays} disabled={running} variant="outline" className="border-[#E87722] text-[#E87722]" data-testid="notif-run-birthdays-btn">
            <Cake className="mr-1.5 h-4 w-4" /> {running ? "Running…" : "Run birthdays now"}
          </Button>
          <Button onClick={runOverdue} disabled={running} variant="outline" className="border-rose-500 text-rose-600" data-testid="notif-run-overdue-btn">
            <AlertTriangle className="mr-1.5 h-4 w-4" /> {running ? "Running…" : "Run overdue now"}
          </Button>
        </div>
      </div>

      {demoMode && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="notif-demo-banner">
          <MessageCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <span className="font-semibold">Demo / simulation mode.</span>{" "}
            WhatsApp messages are composed and logged here but <span className="font-semibold">not actually sent</span>.
            Add your Meta WhatsApp Business Cloud API credentials (Phone Number ID + access token) and set
            <code className="mx-1 rounded bg-amber-100 px-1">WHATSAPP_DEMO_MODE=false</code> to go live.
            Email follows the same pattern — add a <code className="mx-1 rounded bg-amber-100 px-1">RESEND_API_KEY</code> to start sending real emails.
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#E5E2DE] bg-[#F9F6F0] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-muted md:grid">
          <div className="col-span-2">When</div>
          <div className="col-span-1">Channel</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">To</div>
          <div className="col-span-2">Message</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {loading && <div className="px-5 py-10 text-center text-sm text-ink-soft">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-ink-soft" data-testid="notif-empty">No notifications yet. Approve a loan or run the reminder scan to generate messages.</div>
        )}
        {items.map((n) => (
          <div key={n.id} data-testid={`notif-row-${n.id}`}
            className="grid grid-cols-1 gap-2 border-b border-[#E5E2DE] px-5 py-4 last:border-b-0 md:grid-cols-12 md:gap-4">
            <div className="text-xs text-ink-soft md:col-span-2">{new Date(n.created_at).toLocaleString()}</div>
            <div className="md:col-span-1">
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${n.channel === "email" ? "border-violet-300 bg-violet-100 text-violet-900" : "border-emerald-300 bg-emerald-100 text-emerald-900"}`}>
                {n.channel === "email" ? "Email" : "WhatsApp"}
              </span>
            </div>
            <div className="text-sm font-medium text-ink md:col-span-2">{KIND_LABEL[n.kind] || n.kind}</div>
            <div className="text-xs text-ink-soft md:col-span-2 break-all">{n.to || n.to_phone || "—"}</div>
            <div className="text-xs text-ink-soft md:col-span-2">{n.body}</div>
            <div className="md:col-span-2">
              <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_BADGE[n.status] || "bg-stone-100 text-stone-900 border-stone-300"}`} data-testid={`notif-status-${n.id}`}>
                {n.status.replace(/_/g, " ")}
              </span>
            </div>
            <div className="md:col-span-1 md:text-right">
              <button
                type="button"
                onClick={() => resend(n.id)}
                title="Resend this notification"
                data-testid={`notif-resend-${n.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-[#5B2C82] px-2.5 py-1 text-xs font-medium text-[#5B2C82] transition-colors hover:bg-[#5B2C82] hover:text-white"
              >
                <RotateCw className="h-3.5 w-3.5" /> Resend
              </button>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
